import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { Select } from "components/ui/Select/Select";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { LetterTab } from "features/consultations/components/tabs/LetterTab";
import { PrescriptionTab } from "features/consultations/components/tabs/PrescriptionTab";
import { ReferralTab } from "features/consultations/components/tabs/ReferralTab";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { SendMncmsButton } from "features/pregnancy/components/antenatal/SendMncmsButton/SendMncmsButton";
import { FoetalObservationSection } from "features/pregnancy/components/FoetalObservationSection/FoetalObservationSection";
import { MncmsBlockersNotice } from "features/pregnancy/components/messages/MncmsBlockersNotice/MncmsBlockersNotice";
import {
  UTERINE_CONTRACTIONS_OPTIONS,
} from "features/pregnancy/constants/antenatalDropdownOptions";
import { CONSULTATION_TAB_OPTIONS } from "features/pregnancy/constants/consultationTabOptions";
import {
  URINALYSIS_BLOOD_LEUCOCYTES_OPTIONS,
  URINALYSIS_GLUCOSE_OPTIONS,
  URINALYSIS_KETONES_OPTIONS,
  URINALYSIS_NITRITES_OPTIONS,
  URINALYSIS_PROTEIN_OPTIONS,
  withLegacyPositive,
} from "features/pregnancy/constants/urinalysisOptions";
import { useAnteNatalConsultationSave } from "features/pregnancy/hooks/useAnteNatalConsultationSave";
import { useBaselinePrefill } from "features/pregnancy/hooks/useBaselinePrefill";
import { useBmiCalculation } from "features/pregnancy/hooks/useBmiCalculation";
import { useGestationCalculation } from "features/pregnancy/hooks/useGestationCalculation";
import { useMaternityHospitals } from "features/pregnancy/hooks/useMaternityHospitals";
import { useMncmsInboundMessages } from "features/pregnancy/hooks/useMncmsInboundMessages";
import { useMncmsReadiness } from "features/pregnancy/hooks/useMncmsReadiness";
import { usePatientMedicationHistory } from "features/pregnancy/hooks/usePatientMedicationHistory";
import { useSendMncmsMessage } from "features/pregnancy/hooks/useSendMncmsMessage";
import { antenatalFormSchema } from "features/pregnancy/utils/antenatalFormSchema";
import { buildInitialObsState } from "features/pregnancy/utils/buildInitialObsState";
import {
  isEndedFoetus,
  resolveConsultationFoetuses,
} from "features/pregnancy/utils/consultationFoetuses";
import { formatConsultationDate } from "features/pregnancy/utils/formatConsultationDate";
import { formatMedicationHistoryToString } from "features/pregnancy/utils/formatMedicationHistory";
import { getAntenatalDefaultValues } from "features/pregnancy/utils/getAntenatalDefaultValues";
import { getMissingAntenatalMncmsFields } from "features/pregnancy/utils/getMissingMncmsFields";
import { describeMncmsDestination } from "features/pregnancy/utils/receivingHospital";

import style from "./PregnancyAnteNatalConsultationForm.module.css";

/*
 * Guidance only. Fundal height used to be hidden below 24 weeks; Darren asked in review for it and
 * the two foetal fields to be shown at every visit "like existing systems and let doctor decide what
 * to fill in when", with the timing carried by an info hint. 24 weeks is the threshold the form used
 * to enforce, kept so the guidance matches the old behaviour, and the gestation-dependence may come
 * back later ("In time we can make it dependent").
 */
const FUNDAL_HEIGHT_TIMING_HINT =
  "Normally recorded from 24 weeks' gestation. Shown at every visit, so leave it blank until it applies.";

export const PregnancyAnteNatalConsultationForm = ({
  closeView,
  tenantVal,
  pregnancyId,
  patientId,
  anteNatalConsultation,
  pregnancy,
}) => {
  const isEditMode = !!anteNatalConsultation;
  const [activeTab, setActiveTab] = useState("consultation");

  const { data: patient } = usePatientData(patientId);
  // 12-month medication & vaccination history (prescribed + discontinued +
  // vaccinations), shown read-only for reference (KIN-1085). The BE builds the
  // actual MN-CMS Medication field, so this is never submitted.
  const { data: medicationHistory } = usePatientMedicationHistory(patientId);
  const { mutate: sendMncmsMessage, isPending: isSendPending } =
    useSendMncmsMessage(pregnancyId);
  // Shared care is hospital-initiated (KIN-1086): the GP can't send until at
  // least one inbound MN-CMS message (msgtype 58) exists for this pregnancy.
  const { data: inboundMessages } = useMncmsInboundMessages(pregnancyId);
  const isAwaitingInbound = (inboundMessages?.length ?? 0) === 0;
  const userName = localStorage.getItem("kinesin-name");

  /*
   * Two different lists, which this form used to conflate (KIN-2075).
   *
   * `liveFoetuses` is who a visit asks about and who it writes observations for — the babies
   * still continuing. `foetuses` is who this visit has findings for, which on an earlier visit
   * being corrected may include a baby that has since ended. Rendering the second while saving
   * only the first is what lets a correction show those findings without inviting changes to
   * them.
   */
  const liveFoetuses = pregnancy?.foetuses?.filter((f) => f.isActive) ?? [];
  const foetuses = resolveConsultationFoetuses({
    liveFoetuses,
    observations: anteNatalConsultation?.foetalObservations,
  });
  const isMultiple = foetuses.length > 1;

  const form = useForm({
    resolver: zodResolver(antenatalFormSchema),
    defaultValues: {
      ...getAntenatalDefaultValues(anteNatalConsultation),
      foetalObservations: buildInitialObsState(
        foetuses,
        anteNatalConsultation?.foetalObservations
      ),
    },
  });

  useBaselinePrefill({ form, patientId });
  useGestationCalculation({ form, pregnancy, anteNatalConsultation });
  useBmiCalculation(form);

  const {
    consultationId,
    wasSent,
    sendError,
    isPending,
    pendingAction,
    handleSave,
    handleFinish,
    handleFinishAndSend,
    handleRetrySend,
  } = useAnteNatalConsultationSave({
    tenantVal,
    patientId,
    pregnancyId,
    anteNatalConsultation,
    // Deliberately the live list: re-saving a corrected visit must leave an ended foetus's
    // observations exactly as they were.
    foetuses: liveFoetuses,
    sendMncmsMessage,
    form,
    closeView,
  });

  const missingMncmsFields = getMissingAntenatalMncmsFields(
    form.watch(),
    pregnancy,
    patient
  );

  // The hospital this pregnancy's messages actually go to — MSH.6, not the consultant's
  // hospital (KIN-2031).
  const { data: maternityHospitals } = useMaternityHospitals();
  const destinationName = describeMncmsDestination(pregnancy, maternityHospitals);
  const medicationsText = formatMedicationHistoryToString(medicationHistory);
  const foetalObservations = form.watch("foetalObservations") ?? {};
  const foetalObsErrors = form.formState.errors?.foetalObservations;
  // Only re-validate on change once the form has been submitted, so we don't
  // surface "required" errors before the user has tried to submit.
  const revalidateOnChange = form.formState.isSubmitted;
  const consultationDisplayDate = formatConsultationDate(
    anteNatalConsultation?.consultationDate
  );

  /*
   * What the *record* would refuse the send for, asked of the backend rather than restated here
   * (KIN-1943). This covers the things that have nothing to do with the visit being written —
   * the patient's PPSN and address, the sending GP's council number, the pregnancy's EDD method
   * and hospital — which a GP previously only discovered after pressing Send.
   *
   * It does not replace missingMncmsFields, and the two are not duplicates. The endpoint answers
   * for the *saved* consultation, so it cannot see gestation weeks or a blood pressure being
   * typed right now; that list is about the form in front of the GP, this is about everything
   * behind it. Only available once the visit exists, since the endpoint is keyed on its id.
   */
  const { data: readiness } = useMncmsReadiness(consultationId);
  const recordBlockers = readiness?.blockers ?? [];
  /*
   * Undefined readiness never blocks: it means the check has not answered yet, or there is no
   * saved visit to ask about. Disabling Send on a loading state would make the button flicker
   * shut on every render, and refusing to send because we have not asked is worse than sending
   * and being told no.
   */
  const isBlockedByRecord = readiness ? readiness.canSend === false : false;
  const isSendReady = missingMncmsFields.length === 0 && !isBlockedByRecord;

  return (
    <FormProvider {...form} style={{ width: "100%", marginBottom: 0 }}>
      <div className={style.wrapper}>
        <div className={style.consultationHeader}>
          <div className={style.headerLeft}>
            <button
              type="button"
              className={style.backButton}
              aria-label="Back"
              onClick={closeView}
            >
              <ArrowLeft size={16} />
            </button>
            <div className={style.headerInfo}>
              <p className={style.headerLabel}>
                {isEditMode
                  ? "Ante-natal consultation"
                  : "New ante-natal consultation"}
              </p>
              <p className={style.headerDate}>{consultationDisplayDate}</p>
            </div>
          </div>
          <div className={style.headerActions}>
            {isEditMode ? (
              <>
                <Button
                  type="button"
                  onClick={handleFinish}
                  disabled={isPending}
                >
                  Save consultation
                </Button>
                {!!consultationId && (
                  <SendMncmsButton
                  destinationName={destinationName}
                    consultationId={consultationId}
                    isSent={wasSent}
                    isSendPending={(isSendPending || isPending) && pendingAction === "send"}
                    sendError={sendError}
                    isSendReady={isSendReady}
                    missingFields={missingMncmsFields}
                    sendActionLabel="Save, and send"
                    isAwaitingInbound={isAwaitingInbound}
                    onSend={handleFinishAndSend}
                    onRetry={handleRetrySend}
                  />
                )}
              </>
            ) : (
              <>
                <Button
                  type="button"
                  onClick={handleFinish}
                  disabled={isPending}
                >
                  Finish consultation
                </Button>
                <SendMncmsButton
                  destinationName={destinationName}
                  consultationId={consultationId}
                  isSent={wasSent}
                  isSendPending={(isSendPending || isPending) && pendingAction === "send"}
                  sendError={sendError}
                  isSendReady={isSendReady}
                  missingFields={missingMncmsFields}
                  sendActionLabel="Finish, and send"
                  isAwaitingInbound={isAwaitingInbound}
                  onSend={handleFinishAndSend}
                  onRetry={handleRetrySend}
                />
              </>
            )}
          </div>
        </div>

        <div className={style.tabContentArea}>
          {/* Above the form, not beside the Send button: the point is that the GP reads it before
              writing the visit up, rather than after finishing and being refused (KIN-1943). */}
          {/* The Send button already says the hospital has not written yet, so this carries only
              what the practice can act on — otherwise the same sentence appeared twice. */}
          <MncmsBlockersNotice blockers={recordBlockers} showHospitalGate={false} />

          <div className={style.tabSwitcherWrapper}>
            <TabSwitcher
              options={CONSULTATION_TAB_OPTIONS}
              value={activeTab}
              onChange={setActiveTab}
              fullWidth
            />
          </div>

          {activeTab === "consultation" && (
            <div className={style.tabBody}>
              <div className={style.inputGroupGrid4Columns}>
                <div>
                  <label className={style.pregnancyFormLabel}>
                    Consultation type
                  </label>
                  <input
                    readOnly
                    className={`form-control ${style.readOnlyInput}`}
                    value="Ante-natal Visit"
                  />
                </div>
                <div>
                  <label className={style.pregnancyFormLabel}>
                    Date and time
                  </label>
                  <input
                    readOnly
                    className={`form-control ${style.readOnlyInput}`}
                    value={consultationDisplayDate}
                  />
                </div>
                <div>
                  <label className={style.pregnancyFormLabel}>Doctor</label>
                  <input
                    readOnly
                    value={userName ?? ""}
                    className={`form-control ${style.readOnlyInput}`}
                  />
                </div>
                <FormField name="dateOfNextVisitWeeks">
                  <label className={style.pregnancyFormLabel}>
                    Date of next visit
                  </label>
                  <div className={style.unitInput} data-unit="wks">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control"
                      placeholder="e.g. 4"
                      onInput={(e) =>
                        (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                      }
                      {...form.register("dateOfNextVisitWeeks")}
                    />
                  </div>
                  <p className={style.fieldHint}>
                    Number of weeks until next visit. If a review is required
                    within days then please enter that in the clinical note.
                  </p>
                </FormField>
              </div>
              <div className={style.inputGroupGrid4Columns}>
                <div>
                  <label className={style.pregnancyFormLabel}>Gestation</label>
                  <div className={style.gestationRow}>
                    <div
                      className={`${style.unitInput} ${style.gestationInputGrow}`}
                      data-unit="weeks"
                    >
                      <input
                        type="text"
                        className={`form-control ${style.readOnlyInput}`}
                        readOnly
                        value={form.watch("gestationWeeks")}
                        onChange={() => {}}
                      />
                    </div>
                    <span className={style.gestationSeparator}>+</span>
                    <div className={style.unitInput} data-unit="days">
                      <input
                        type="text"
                        className={`form-control ${style.gestationInput} ${style.readOnlyInput}`}
                        readOnly
                        value={form.watch("gestationDays")}
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className={style.pregnancyFormLabel}>Gravida</label>
                  <input
                    type="text"
                    className={`form-control ${style.readOnlyInput}`}
                    readOnly
                    value={pregnancy?.gravida ?? ""}
                    onChange={() => {}}
                  />
                </div>
                <div>
                  <label className={style.pregnancyFormLabel}>
                    Para (Parity)
                  </label>
                  <input
                    type="text"
                    className={`form-control ${style.readOnlyInput}`}
                    readOnly
                    value={pregnancy?.para ?? ""}
                    onChange={() => {}}
                  />
                </div>
              </div>

              <form className={style.consultationForm} onSubmit={handleSave}>
                <h3 className={style.pregnancyFormSectionTitle}>
                  Maternal Exam
                </h3>
                <div className={style.inputGroupGrid4Columns}>
                  <FormField name="bloodPressureSystolic">
                    <label className={style.pregnancyFormLabel}>
                      BP Systolic <span className={style.requiredMark}>*</span>
                    </label>
                    <div className={style.unitInput} data-unit="mmHg">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 120"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          ))
                        }
                        {...form.register("bloodPressureSystolic")}
                      />
                    </div>
                  </FormField>
                  <FormField name="bloodPressureDiastolic">
                    <label className={style.pregnancyFormLabel}>
                      BP Diastolic <span className={style.requiredMark}>*</span>
                    </label>
                    <div className={style.unitInput} data-unit="mmHg">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 80"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          ))
                        }
                        {...form.register("bloodPressureDiastolic")}
                      />
                    </div>
                  </FormField>
                  <FormField name="ankleOedema">
                    <label className={style.pregnancyFormLabel}>
                      Ankle oedema
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Oedema, Bilateral Pedal 4+ (8mm) Pitting"
                      {...form.register("ankleOedema")}
                    />
                  </FormField>
                  <FormField name="uterineContractions">
                    <label className={style.pregnancyFormLabel}>
                      Uterine contractions
                    </label>
                    <Select
                      name="uterineContractions"
                      options={UTERINE_CONTRACTIONS_OPTIONS}
                      value={form.watch("uterineContractions")}
                      onChange={(value) =>
                        form.setValue("uterineContractions", value)
                      }
                      placeholder="Select"
                      fullWidth
                    />
                  </FormField>
                  <FormField name="maternalWeightKg">
                    <label className={style.pregnancyFormLabel}>
                      Maternal weight
                    </label>
                    <div className={style.unitInput} data-unit="kg">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        placeholder="e.g. 72.5"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9.]/g,
                            ""
                          ))
                        }
                        {...form.register("maternalWeightKg")}
                      />
                    </div>
                  </FormField>
                  <FormField name="maternalHeightCm">
                    <label className={style.pregnancyFormLabel}>Height</label>
                    <div className={style.unitInput} data-unit="cm">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 165"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          ))
                        }
                        {...form.register("maternalHeightCm")}
                      />
                    </div>
                  </FormField>
                  <FormField name="bmi">
                    <label className={style.pregnancyFormLabel}>BMI</label>
                    <div className={style.unitInput} data-unit="kg/m²">
                      <input
                        type="text"
                        inputMode="decimal"
                        className={`form-control ${style.readOnlyInput}`}
                        placeholder="Auto-calculated"
                        readOnly
                        {...form.register("bmi")}
                      />
                    </div>
                  </FormField>
                  <FormField name="haemoglobin">
                    <label className={style.pregnancyFormLabel}>
                      Haemoglobin
                    </label>
                    <div className={style.unitInput} data-unit="g/dl">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        placeholder="e.g. 11.5"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9.]/g,
                            ""
                          ))
                        }
                        {...form.register("haemoglobin")}
                      />
                    </div>
                  </FormField>
                  <FormField name="fundalHeightCm">
                    <label className={style.pregnancyFormLabel}>
                      Symphysis–fundal height
                      <InfoTooltip text={FUNDAL_HEIGHT_TIMING_HINT} />
                    </label>
                    <div className={style.cmInput}>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 28"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          ))
                        }
                        {...form.register("fundalHeightCm")}
                      />
                    </div>
                  </FormField>
                </div>

                <h3 className={style.pregnancyFormSectionTitle}>Urinalysis</h3>
                <div className={style.inputGroupGrid}>
                  <FormField name="urinalysisGlucose">
                    <label className={style.pregnancyFormLabel}>Glucose</label>
                    <Select
                      name="urinalysisGlucose"
                      options={URINALYSIS_GLUCOSE_OPTIONS}
                      value={form.watch("urinalysisGlucose")}
                      onChange={(value) =>
                        form.setValue("urinalysisGlucose", value)
                      }
                      placeholder={null}
                      fullWidth
                    />
                  </FormField>
                  <FormField name="urinalysisProtein">
                    <label className={style.pregnancyFormLabel}>Protein</label>
                    <Select
                      name="urinalysisProtein"
                      options={URINALYSIS_PROTEIN_OPTIONS}
                      value={form.watch("urinalysisProtein")}
                      onChange={(value) =>
                        form.setValue("urinalysisProtein", value)
                      }
                      placeholder="Select"
                      fullWidth
                    />
                  </FormField>
                  {/* A dropdown, not the lone tick box it used to be — Darren: "its odd to have
                      1 random tick box and makes it be missed by a user". It sits in the grid
                      with the rest for the same reason. Blank is offered so a visit that never
                      recorded nitrites stays blank instead of reading as a negative result. */}
                  <FormField name="urinalysisNitrites">
                    <label className={style.pregnancyFormLabel}>Nitrites</label>
                    <Select
                      name="urinalysisNitrites"
                      options={URINALYSIS_NITRITES_OPTIONS}
                      value={form.watch("urinalysisNitrites")}
                      onChange={(value) =>
                        form.setValue("urinalysisNitrites", value)
                      }
                      placeholder="Not recorded"
                      fullWidth
                    />
                  </FormField>
                </div>
                {/* Graded, not ticked (KIN-2038). Darren: a tick box for these "will pull us
                    up". Ketones grade in pluses, blood and leucocytes in words, which is how
                    the dipstick reads them. */}
                <div className={style.inputGroupGrid}>
                  {[
                    {
                      name: "urinalysisKetones",
                      label: "Ketones",
                      options: URINALYSIS_KETONES_OPTIONS,
                    },
                    {
                      name: "urinalysisBlood",
                      label: "Blood",
                      options: URINALYSIS_BLOOD_LEUCOCYTES_OPTIONS,
                    },
                    {
                      name: "urinalysisLeucocytes",
                      label: "Leucocytes",
                      options: URINALYSIS_BLOOD_LEUCOCYTES_OPTIONS,
                    },
                  ].map(({ name, label, options }) => (
                    <FormField key={name} name={name}>
                      <label className={style.pregnancyFormLabel}>{label}</label>
                      <Select
                        name={name}
                        options={withLegacyPositive(options, form.watch(name))}
                        value={form.watch(name)}
                        onChange={(value) => form.setValue(name, value)}
                        placeholder={null}
                        fullWidth
                      />
                    </FormField>
                  ))}
                </div>

                <h3 className={style.pregnancyFormSectionTitle}>Foetal Exam</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "12px" }}>
                  {foetuses.map((foetus) => {
                    const identifier = foetus.foetusIdentifier;
                    const hasEnded = isEndedFoetus(foetus);

                    return (
                      <FoetalObservationSection
                        key={identifier}
                        foetusIdentifier={isMultiple ? identifier : null}
                        readOnly={hasEnded}
                        outcome={foetus.outcome}
                        values={foetalObservations[identifier] ?? {}}
                        errors={foetalObsErrors?.[identifier]}
                        onChange={(updated) =>
                          form.setValue(
                            "foetalObservations",
                            { ...foetalObservations, [identifier]: updated },
                            { shouldValidate: revalidateOnChange }
                          )
                        }
                        formStyle={style}
                      />
                    );
                  })}
                </div>

                <h3 className={style.pregnancyFormSectionTitle}>
                  Clinical notes
                </h3>
                <div className={style.inputGroupGrid2Columns}>
                  <FormField name="impression">
                    <label className={style.pregnancyFormLabel}>
                      Impression
                    </label>
                    <textarea
                      className="form-control textareaform-control"
                      style={{ minHeight: "120px" }}
                      placeholder="e.g. Uncomplicated pregnancy, progressing well"
                      {...form.register("impression")}
                    />
                  </FormField>
                  <FormField name="plan">
                    <label className={style.pregnancyFormLabel}>Plan</label>
                    <textarea
                      className="form-control textareaform-control"
                      style={{ minHeight: "120px" }}
                      placeholder="e.g. Routine ante-natal bloods at 28 weeks, GTT arranged"
                      {...form.register("plan")}
                    />
                  </FormField>
                  <FormField name="additionalInfo">
                    <label className={style.pregnancyFormLabel}>
                      Additional information
                    </label>
                    <textarea
                      className="form-control textareaform-control"
                      style={{ minHeight: "120px" }}
                      placeholder="e.g. Patient reports mild back pain, advised physiotherapy"
                      {...form.register("additionalInfo")}
                    />
                  </FormField>
                  {/* Read-only reference: 12-month medication & vaccination
                      history. Not part of the form and never submitted — the BE
                      compiles the MN-CMS Medication field from this same data. */}
                  <div>
                    <label className={style.pregnancyFormLabel}>Medications</label>
                    <textarea
                      className="form-control textareaform-control"
                      style={{ minHeight: "120px" }}
                      value={medicationsText}
                      placeholder="No recent medications or vaccinations"
                      readOnly
                      disabled
                    />
                    <p className={style.fieldHint}>
                      Medication &amp; vaccination history (last 12 months) — for
                      reference only.
                    </p>
                  </div>
                </div>
              </form>
            </div>
          )}

          {activeTab === "referrals" && (
            <div className={style.externalTabBody}>
              <ReferralTab
                consultationId={consultationId}
                formWrapperStyle={{
                  padding: 0,
                  border: "1px solid var(--gray-200)",
                  borderRadius: "12px",
                }}
                resetOnSuccess
                showAllReferrals
              />
            </div>
          )}

          {activeTab === "letters" && (
            <div className={style.externalTabBody}>
              {/* consultationId intentionally null — pregnancy consultations are a separate
                  entity from general consultations; the BE cannot link them and throws if
                  a pregnancy consultationId is passed to the letter endpoint */}
              <LetterTab consultationId={null} />
            </div>
          )}

          {activeTab === "prescriptions" && (
            <div className={style.externalTabBody}>
              <PrescriptionTab consultationId={consultationId} resetOnSuccess />
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
};
